import { PUBLIC_URL } from 'config';
import apiRequest from 'services/api/api.config';
import { AppChannel, Channel } from 'utils/config.config';
import { getCookieValue } from 'utils/functions/cookie';
import { v4 as uuidV4 } from 'uuid';
import {
  accessUserEndpoint,
  authAccountKey,
  authSiteIdKey,
  authTokenKey,
  authTokenKeyApp,
  codePromozione,
  promoCodeLocalStorage,
} from './session.config';
import { acquireEndcallbackUrlFromQueryParams } from './session.helpers';
import { Homepage, SessionReducerShape, SisalSiteInfo } from './session.types';

async function getPublicIp(): Promise<string> {
  const requestIp = async (apiServiceEndpoint: string) => {
    try {
      const response = await fetch(apiServiceEndpoint, { cache: 'no-store' });
      if (!response.ok) {
        return '';
      }

      return (await response.text()).trim();
    } catch {
      return '';
    }
  };

  return (
    (await requestIp('https://checkip.amazonaws.com/')) ||
    (await requestIp('https://icanhazip.com/')) ||
    (await requestIp('https://api64.ipify.org/')) ||
    ''
  );
}

function getJwtFromCookie() {
  const jwt = getCookieValue('JWT_ar');
  return jwt === '' ? null : jwt;
}

export async function getAuthToken(
  searchParams: URLSearchParams,
  ipAddress = '',
) {
  let authToken = searchParams.get(authTokenKey) ?? getJwtFromCookie();
  const authTokenApp = searchParams.get(authTokenKeyApp);
  const authAccount = searchParams.get(authAccountKey);
  const siteId = searchParams.get(authSiteIdKey);

  if (typeof authToken === 'string') {
    // Remove the previous auth serialized if it was present.
    window.localStorage.removeItem(authTokenKey);

    //Set localStorage key to show VerifyContactAlertModal
    window.localStorage.setItem('firstRender', 'true');
    return {
      authToken,
      from: 'url',
    };
  }

  if (typeof authTokenApp === 'string' && typeof authAccount === 'string') {
    // Remove the previous auth serialized if it was present.
    window.localStorage.removeItem(authTokenKey);

    const params = {
      billCode: authAccount,
      token: authTokenApp,
      infoChannel: window.navigator.userAgent,
      ipAddress,
      channel: siteId ? siteId : '62',
      requestId: uuidV4(),
    };

    authToken = await apiRequest.post(accessUserEndpoint, params).then(
      (success: any) => {
        return success.data.tokenSession;
      },
      (error: any) => {
        console.error(error, accessUserEndpoint);
      },
    );

    return {
      authToken,
      from: 'url-app',
    };
  }

  authToken = window.localStorage.getItem(authTokenKey);

  return {
    authToken,
    from: 'local-storage',
  };
}

export const getSisalSiteInfo = (
  searchParams: URLSearchParams,
  navigator: Navigator,
) => {
  const sisalSiteInfo: SisalSiteInfo = {
    channel: 0,
    channelInfo: navigator.userAgent,
  };

  // https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams/get
  const maybeDefinedSiteIdFromSearchParams: number = parseInt(
    searchParams.get('siteid') ?? '',
    10,
  );

  const hasSiteIdBeenProvidedThroughSearchParams = !Number.isNaN(
    maybeDefinedSiteIdFromSearchParams,
  );

  if (hasSiteIdBeenProvidedThroughSearchParams) {
    sisalSiteInfo.channel = maybeDefinedSiteIdFromSearchParams as Channel;
  } else {
    sisalSiteInfo.channel = AppChannel.PortaleWebFullResponsiveWwwSisalIt;
  }

  if (sisalSiteInfo.channel === AppChannel.PortaleWebFullResponsiveWwwSisalIt) {
    const maybeDefinedUrlRedirect: string | null =
      searchParams.get('urlRedirect');

    if (maybeDefinedUrlRedirect) {
      sisalSiteInfo.urlRedirect = maybeDefinedUrlRedirect;
    }
  }

  return sisalSiteInfo;
};

export async function getRechargePromocode() {
  const searchParams = new URLSearchParams(window.location.search);
  const promoCode: string | null = searchParams.get(codePromozione);

  if (typeof promoCode === 'string') {
    return { promoCode: promoCode };
  }
  const promoCodeStorage = getCookieValue(promoCodeLocalStorage);

  if (promoCodeStorage) {
    return { promoCode: promoCodeStorage };
  }
  return { promoCode: '' };
}

export async function getSessionInfo(): Promise<Partial<SessionReducerShape>> {
  const searchParams = new URLSearchParams(window.location.search);

  const homepage: Homepage = {
    pathname: '',
    search: '',
    host: '',
    protocol: '',
    href: '',
  };

  if (PUBLIC_URL) {
    try {
      let publicURL;
      if (import.meta.env.DEV) {
        publicURL = new URL(PUBLIC_URL, `http://10.224.12.99:5174/`);
      } else {
        publicURL = new URL(PUBLIC_URL);
      }

      homepage.pathname = publicURL.pathname;
      homepage.search = publicURL.host;
      homepage.host = publicURL.host;
      homepage.protocol = publicURL.protocol;
      homepage.href = publicURL.href;
    } catch (parseError) {
      throw new Error(`Invalid PUBLIC_URL provided ${parseError?.message}`);
    }
  }

  const sisalSiteInfo = getSisalSiteInfo(searchParams, window.navigator);
  const ipAddress = await getPublicIp();

  const auth = await getAuthToken(searchParams, ipAddress);

  return {
    info: {
      ipAddress,
      homepage,
      // camelCase key, use 'endCallbackUrlKey' to get the query params key.
      endCallbackUrl: acquireEndcallbackUrlFromQueryParams(searchParams),
      ...sisalSiteInfo,
    },
    auth,
  };
}
